import { API_BASE_URL } from "../../../config";
import { fetchRouteRegistry } from "../../Utils/fetchRouteRegistry";
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

const todayStr = () => {
    const d = new Date();
    const pad = (x) => String(x).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

// ลงทะเบียนขึ้น-กลับบรรจุ (cargo registration) — vector 2026-07-10/13: checkpoint ขึ้นบรรจุ
// (ทะเบียนรถ/สาย + จำนวนหลอด/บด + เวลา) และตอนกลับ (จำนวนเหลือ + เวลา) ต่อรอบ (report) เดียว
// + บันทึกบรรจุเติมกลางทางได้หลายครั้ง. สาย/ทะเบียนรถต้อง exact-match กับ registry จริง
// (deliveryCar tag, auth-module) — validate ที่ backend, ฟอร์มนี้ใช้ dropdown เดียวกันเพื่อกันพิมพ์ผิด
const CargoRegistrationAdmin = () => {
    const navigate = useNavigate();

    const [searchDate, setSearchDate] = useState(todayStr());
    const [reports, setReports] = useState([]);
    const [loadingReports, setLoadingReports] = useState(false);

    const [vehicleOptions, setVehicleOptions] = useState([]);
    const [vehicleError, setVehicleError] = useState('');

    const [selectedReportCode, setSelectedReportCode] = useState('');
    const [cargo, setCargo] = useState(null); // null = not yet loaded / no registration exists
    const [loadingCargo, setLoadingCargo] = useState(false);
    const [notFound, setNotFound] = useState(false);

    const [loadOutForm, setLoadOutForm] = useState({ vehiclePlate: '', loadOutTubeQty: '', loadOutCrushedQty: '' });
    const [returnForm, setReturnForm] = useState({ returnTubeQty: '', returnCrushedQty: '' });
    const [refillForm, setRefillForm] = useState({ tubeQty: '', crushedQty: '' });
    const [busy, setBusy] = useState(false);
    const [formError, setFormError] = useState('');

    const checkToken = useCallback(() => {
        const user = localStorage.getItem('user');
        if (!user) { navigate('/login'); return null; }
        return JSON.parse(user)?.token;
    }, [navigate]);

    const fetchReports = useCallback(async (d) => {
        const token = checkToken();
        if (!token) return;
        setLoadingReports(true);
        try {
            const res = await fetch(`${API_BASE_URL}/saledata/api/module/reports/by-date?startDate=${d}&endDate=${d}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            const data = res.ok ? await res.json() : [];
            setReports(Array.isArray(data) ? data : []);
        } finally {
            setLoadingReports(false);
        }
    }, [checkToken]);

    const fetchVehicleOptions = useCallback(async () => {
        const token = checkToken();
        if (!token) return;
        setVehicleError('');
        try {
            const { response: res, rows } = await fetchRouteRegistry(token);
            if (!res.ok) {
                setVehicleOptions([]);
                const reason = (res.status === 401 || res.status === 403) ? 'สิทธิ์ไม่พอ' : 'เกิดข้อผิดพลาด';
                setVehicleError(`โหลดรายชื่อสายไม่สำเร็จ (${res.status} — ${reason})`);
                return;
            }
            setVehicleOptions(rows);
        } catch (e) {
            setVehicleOptions([]);
            setVehicleError('โหลดรายชื่อสายไม่สำเร็จ');
        }
    }, [checkToken]);

    const fetchCargo = useCallback(async (reportCode) => {
        const token = checkToken();
        if (!token || !reportCode) return;
        setLoadingCargo(true);
        setNotFound(false);
        setCargo(null);
        try {
            const res = await fetch(`${API_BASE_URL}/saledata/api/module/cargo-registration/report/${encodeURIComponent(reportCode)}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            if (res.status === 404) {
                setNotFound(true);
                return;
            }
            const data = res.ok ? await res.json() : null;
            setCargo(data);
        } finally {
            setLoadingCargo(false);
        }
    }, [checkToken]);

    useEffect(() => {
        fetchReports(searchDate);
        setSelectedReportCode('');
        setCargo(null);
        setNotFound(false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchDate]);

    useEffect(() => {
        fetchVehicleOptions();
    }, [fetchVehicleOptions]);

    useEffect(() => {
        if (selectedReportCode) fetchCargo(selectedReportCode);
    }, [selectedReportCode, fetchCargo]);

    const submitLoadOut = async () => {
        const token = checkToken();
        if (!token) return;
        setFormError('');
        if (!loadOutForm.vehiclePlate) { setFormError('กรุณาเลือกสาย/ทะเบียนรถ'); return; }
        setBusy(true);
        try {
            const res = await fetch(`${API_BASE_URL}/saledata/api/module/cargo-registration/load-out`, {
                method: 'POST',
                headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    reportCode: selectedReportCode,
                    vehiclePlate: loadOutForm.vehiclePlate,
                    loadOutTubeQty: loadOutForm.loadOutTubeQty === '' ? null : Number(loadOutForm.loadOutTubeQty),
                    loadOutCrushedQty: loadOutForm.loadOutCrushedQty === '' ? null : Number(loadOutForm.loadOutCrushedQty),
                }),
            });
            if (!res.ok) { const t = await res.text(); throw new Error(t || `HTTP ${res.status}`); }
            await fetchCargo(selectedReportCode);
        } catch (e) {
            setFormError('บันทึกขึ้นบรรจุไม่สำเร็จ: ' + (e.message || ''));
        } finally {
            setBusy(false);
        }
    };

    const submitReturn = async () => {
        const token = checkToken();
        if (!token || !cargo) return;
        setFormError('');
        setBusy(true);
        try {
            const res = await fetch(`${API_BASE_URL}/saledata/api/module/cargo-registration/${cargo.id}/return`, {
                method: 'PUT',
                headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    returnTubeQty: returnForm.returnTubeQty === '' ? null : Number(returnForm.returnTubeQty),
                    returnCrushedQty: returnForm.returnCrushedQty === '' ? null : Number(returnForm.returnCrushedQty),
                }),
            });
            if (!res.ok) { const t = await res.text(); throw new Error(t || `HTTP ${res.status}`); }
            await fetchCargo(selectedReportCode);
        } catch (e) {
            setFormError('บันทึกจุดกลับไม่สำเร็จ: ' + (e.message || ''));
        } finally {
            setBusy(false);
        }
    };

    const submitRefill = async () => {
        const token = checkToken();
        if (!token || !cargo) return;
        setFormError('');
        setBusy(true);
        try {
            const res = await fetch(`${API_BASE_URL}/saledata/api/module/cargo-registration/${cargo.id}/refill`, {
                method: 'POST',
                headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    tubeQty: refillForm.tubeQty === '' ? null : Number(refillForm.tubeQty),
                    crushedQty: refillForm.crushedQty === '' ? null : Number(refillForm.crushedQty),
                }),
            });
            if (!res.ok) { const t = await res.text(); throw new Error(t || `HTTP ${res.status}`); }
            setRefillForm({ tubeQty: '', crushedQty: '' });
            await fetchCargo(selectedReportCode);
        } catch (e) {
            setFormError('บันทึกบรรจุเติมไม่สำเร็จ: ' + (e.message || ''));
        } finally {
            setBusy(false);
        }
    };

    const rowStyle = { borderBottom: '1px solid #E5E5E5', padding: '8px 12px' };
    const headerStyle = { background: '#E5E5E5', fontWeight: 700, borderBottom: '1px solid #E5E5E5', padding: '8px 12px', fontSize: '13px', color: '#000' };
    const sectionStyle = { background: '#fff', border: '1px solid #E5E5E5', borderRadius: '8px', boxShadow: '0 1px 4px rgba(0,0,0,0.06)', marginBottom: '24px', overflow: 'hidden' };
    const sectionHeaderStyle = { background: '#E5E5E5', color: '#000', padding: '10px 16px', fontWeight: 700, fontSize: '14px', borderBottom: '1px solid #E5E5E5' };
    const labelStyle = { color: '#6B6B6B', fontSize: '13px' };
    const inputStyle = { padding: '6px 10px', borderRadius: '4px', border: '1px solid #E5E5E5', fontSize: '13px' };
    const btnPrimary = { background: '#E60012', color: '#fff', border: 'none', borderRadius: '4px', padding: '6px 16px', fontSize: '13px', fontWeight: 700, cursor: 'pointer' };
    const btnSecondary = { background: '#6B6B6B', color: '#fff', border: 'none', borderRadius: '4px', padding: '6px 16px', fontSize: '13px', fontWeight: 700, cursor: 'pointer' };

    return (
        <div className="btc-container" style={{ maxWidth: '900px', margin: '0 auto' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '20px' }}>
                <button style={{ ...btnSecondary, padding: '4px 10px' }} onClick={() => navigate(-1)}>← กลับ</button>
                <h1 style={{ margin: 0, fontSize: '18px', color: '#000' }}>ลงทะเบียนขึ้น-กลับบรรจุ</h1>
            </div>

            <div style={sectionStyle}>
                <div style={sectionHeaderStyle}>เลือกรอบ (report)</div>
                <div style={{ padding: '16px', display: 'flex', gap: '16px', flexWrap: 'wrap', alignItems: 'center' }}>
                    <label style={labelStyle}>วันที่:</label>
                    <input type="date" value={searchDate} onChange={e => setSearchDate(e.target.value)} style={inputStyle} />
                    <label style={labelStyle}>รอบ:</label>
                    <select value={selectedReportCode} onChange={e => setSelectedReportCode(e.target.value)} style={{ ...inputStyle, minWidth: '260px' }}>
                        <option value="">-- เลือกรอบ --</option>
                        {reports.map(r => (
                            <option key={r.reportCode} value={r.reportCode}>
                                {(r.nameuser || r.username || r.sellerId)}{r.isAgent ? ' (Agent)' : ''} — {r.reportCode}
                            </option>
                        ))}
                    </select>
                    {loadingReports && <span style={labelStyle}>กำลังโหลด...</span>}
                </div>
            </div>

            {selectedReportCode && loadingCargo && (
                <p style={labelStyle}>กำลังโหลดข้อมูล...</p>
            )}

            {formError && <p style={{ color: '#E60012', fontSize: '13px' }}>{formError}</p>}

            {selectedReportCode && !loadingCargo && notFound && (
                <div style={sectionStyle}>
                    <div style={sectionHeaderStyle}>ขึ้นบรรจุ (ยังไม่มีการลงทะเบียน)</div>
                    <div style={{ padding: '16px', display: 'flex', flexDirection: 'column', gap: '12px', maxWidth: '360px' }}>
                        <label style={labelStyle}>สาย / ทะเบียนรถ</label>
                        <select
                            value={loadOutForm.vehiclePlate}
                            onChange={e => setLoadOutForm(f => ({ ...f, vehiclePlate: e.target.value }))}
                            style={inputStyle}
                        >
                            <option value="">-- เลือกสาย --</option>
                            {vehicleOptions.map(v => (
                                <option key={v.uid} value={v.uid}>{v.nameuser || v.username}</option>
                            ))}
                        </select>
                        {vehicleError && <p style={{ color: '#E60012', fontSize: '13px' }}>{vehicleError}</p>}
                        <label style={labelStyle}>จำนวนหลอด (ขึ้น)</label>
                        <input type="number" style={inputStyle} value={loadOutForm.loadOutTubeQty}
                            onChange={e => setLoadOutForm(f => ({ ...f, loadOutTubeQty: e.target.value }))} />
                        <label style={labelStyle}>จำนวนบด (ขึ้น)</label>
                        <input type="number" style={inputStyle} value={loadOutForm.loadOutCrushedQty}
                            onChange={e => setLoadOutForm(f => ({ ...f, loadOutCrushedQty: e.target.value }))} />
                        <button style={btnPrimary} onClick={submitLoadOut} disabled={busy}>
                            {busy ? 'กำลังบันทึก...' : 'บันทึกขึ้นบรรจุ'}
                        </button>
                    </div>
                </div>
            )}

            {selectedReportCode && !loadingCargo && cargo && (
                <div style={sectionStyle}>
                    <div style={sectionHeaderStyle}>
                        {cargo.vehicleName || cargo.vehiclePlate} — {cargo.returnTime ? 'ปิดรอบแล้ว' : 'อยู่ระหว่างรอบ'}
                    </div>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                        <thead>
                            <tr>
                                <th style={headerStyle}></th>
                                <th style={headerStyle}>หลอด</th>
                                <th style={headerStyle}>บด</th>
                                <th style={headerStyle}>เวลา</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td style={rowStyle}>ขึ้นบรรจุ</td>
                                <td style={rowStyle}>{cargo.loadOutTubeQty ?? '-'}</td>
                                <td style={rowStyle}>{cargo.loadOutCrushedQty ?? '-'}</td>
                                <td style={rowStyle}>{cargo.loadOutTime ? new Date(cargo.loadOutTime).toLocaleTimeString('th-TH') : '-'}</td>
                            </tr>
                            {(cargo.refills || []).map(r => (
                                <tr key={r.id}>
                                    <td style={rowStyle}>บรรจุเติม</td>
                                    <td style={rowStyle}>{r.tubeQty ?? '-'}</td>
                                    <td style={rowStyle}>{r.crushedQty ?? '-'}</td>
                                    <td style={rowStyle}>{r.refillTime ? new Date(r.refillTime).toLocaleTimeString('th-TH') : '-'}</td>
                                </tr>
                            ))}
                            <tr>
                                <td style={rowStyle}>กลับ</td>
                                <td style={rowStyle}>{cargo.returnTubeQty ?? '-'}</td>
                                <td style={rowStyle}>{cargo.returnCrushedQty ?? '-'}</td>
                                <td style={rowStyle}>{cargo.returnTime ? new Date(cargo.returnTime).toLocaleTimeString('th-TH') : '-'}</td>
                            </tr>
                        </tbody>
                    </table>

                    {!cargo.returnTime && (
                        <div style={{ padding: '16px', display: 'flex', gap: '32px', flexWrap: 'wrap' }}>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', minWidth: '220px' }}>
                                <span style={{ ...labelStyle, fontWeight: 700, color: '#000' }}>บรรจุเติมกลางทาง</span>
                                <label style={labelStyle}>หลอด</label>
                                <input type="number" style={inputStyle} value={refillForm.tubeQty}
                                    onChange={e => setRefillForm(f => ({ ...f, tubeQty: e.target.value }))} />
                                <label style={labelStyle}>บด</label>
                                <input type="number" style={inputStyle} value={refillForm.crushedQty}
                                    onChange={e => setRefillForm(f => ({ ...f, crushedQty: e.target.value }))} />
                                <button style={btnSecondary} onClick={submitRefill} disabled={busy}>
                                    {busy ? '...' : 'บันทึกบรรจุเติม'}
                                </button>
                            </div>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', minWidth: '220px' }}>
                                <span style={{ ...labelStyle, fontWeight: 700, color: '#000' }}>บันทึกจุดกลับ</span>
                                <label style={labelStyle}>หลอด (เหลือ)</label>
                                <input type="number" style={inputStyle} value={returnForm.returnTubeQty}
                                    onChange={e => setReturnForm(f => ({ ...f, returnTubeQty: e.target.value }))} />
                                <label style={labelStyle}>บด (เหลือ)</label>
                                <input type="number" style={inputStyle} value={returnForm.returnCrushedQty}
                                    onChange={e => setReturnForm(f => ({ ...f, returnCrushedQty: e.target.value }))} />
                                <button style={btnPrimary} onClick={submitReturn} disabled={busy}>
                                    {busy ? 'กำลังบันทึก...' : 'บันทึกจุดกลับ (ปิดรอบ)'}
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default CargoRegistrationAdmin;
